/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useEffect, useState} from 'react'
import {useAuth} from '../../../../modules/auth'
import {
  collection,
  doc,
  onSnapshot,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  addDoc,
} from 'firebase/firestore'
import db from '../../../../../services/firebase'
import 'flatpickr/dist/themes/material_green.css'
import {TestListLoading} from '../../../tests/components/loading/TestListLoading'
import EditTask from '../../../tasks/components/EditTask'
import AddTask from '../../../tasks/components/AddTask'
import {showErrorToast, showSuccessToast} from 'app/components/ShowToast'
import ConfirmationModal from 'app/components/ConfirmationModal'
import Button from 'app/components/Button'
// import ExportButton from 'app/components/ExportButton'
import OwnerDisplay from 'app/components/OwnerDisplay'
import FormatTimestamp from 'app/components/FormatTimestamp'
// import {ExportTrialUsers} from '../../../audit/components/utils/ExportTrialUsers'

type Props = {
  className: string
}

const TaskList: React.FC<Props> = ({className}) => {
  const {currentUser} = useAuth()
  const [tasks, setTasks] = useState<any[]>([])
  const [sortBy, setSortBy] = useState('')
  const [sortDirection, setSortDirection] = useState('asc')
  const [showEmptyState, setShowEmptyState] = useState<boolean>(false)
  const [isModalOpen2, setIsModalOpen2] = useState(false)
  const [taskID, setTaskID] = useState('')
  const [checkedTasks, setCheckedTasks] = useState<string[]>([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [filteredTasks, setFilteredTasks] = useState<any[]>([])
  const [subTab, setSubTab] = useState('open')
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [toDeleteTask, setToDeleteTask] = useState<any>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    const filterTasks = () => {
      let filtered = tasks

      switch (subTab) {
        case 'open':
          filtered = filtered
          break
        case 'completed':
          filtered = filtered.filter((task) => task.status === 'Completed')
          break
        case 'pending':
          filtered = filtered.filter((task) => task.status !== 'Completed')
          break
        case 'overdue':
          filtered = filtered.filter(
            (task) => task.status !== 'Completed' && new Date(task.duedate) < new Date()
          )
          break
        case 'myTasks':
          filtered = filtered.filter(
            (task) => task.owner?.toLowerCase() === currentUser?.userName?.toLowerCase()
          )
          break
      }

      setShowEmptyState(filtered.length === 0)
      setFilteredTasks(filtered)
    }

    filterTasks()
  }, [tasks, subTab, currentUser])

  useEffect(() => {
    getActivities()
  }, [])

  const getActivities = async () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const controlsCollectionRef = collection(tenantRef, 'tasklists')
      const unsubscribe = onSnapshot(controlsCollectionRef, (snapshot) => {
        const controlsData = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        }))
        setTasks(controlsData)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error getting controls:', error)
    }
  }

  useEffect(() => {
    // Delay the rendering of the empty state for 3 seconds
    const timeoutId = setTimeout(() => {
      setShowEmptyState(true)
    }, 1000)
    return () => clearTimeout(timeoutId)
  }, [])

  useEffect(() => {
    const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]')
    if (window.bootstrap && window.bootstrap.Tooltip) {
      tooltipTriggerList.forEach((tooltipTriggerEl) => {
        new window.bootstrap.Tooltip(tooltipTriggerEl)
      })
    }
  }, [])

  const priorityOrder = {
    Low: 1,
    Medium: 2,
    High: 3,
  }

  const handleSort = (column) => {
    const newSortDirection = column === sortBy && sortDirection === 'asc' ? 'desc' : 'asc'
    setSortBy(column)
    setSortDirection(newSortDirection)
  }

  const renderSortIcon = (column) => {
    if (column === sortBy) {
      return (
        <span
          className={`ms-1 icon-xxs text-muted ${
            sortDirection === 'asc' ? 'bi bi-arrow-up' : 'bi bi-arrow-down'
          }`}
        />
      )
    }
    return null
  }

  const currentDate = new Date()
  const year = currentDate.getFullYear()
  const month = currentDate.getMonth() + 1
  const day = currentDate.getDate()

  const todaysDate = month + '/' + day + '/' + year

  const handleMarkAsComplete = async (taskId, taskName) => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const taskRef = doc(db, 'tenants', tenantId, 'tasklists', taskId)
      await updateDoc(taskRef, {status: 'Completed'})
      getActivities()

      const updatedTasks = tasks.filter((task) => task.id !== taskId)
      setTasks(updatedTasks)
      const tenantRef = doc(db, 'tenants', currentUser?.tenantId!)
      const activitiesRef = collection(tenantRef, 'activities')
      await addDoc(activitiesRef, {
        message: `${currentUser?.userName} has completed the task '${taskName}'`,
        timestamp: serverTimestamp(),
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        action: 'Control added',
        target: 'Controls',
        email: currentUser?.email,
        username: currentUser?.userName,
        date: todaysDate,
      })
      showSuccessToast({message: 'Task has been marked as completed!'})
    } catch (error) {
      showErrorToast({message: 'Error updating task status'})
    }
  }

  const handleMarkAsIncomplete = async (taskId, taskName) => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const taskRef = doc(db, 'tenants', tenantId, 'tasklists', taskId)
      await updateDoc(taskRef, {status: 'Incomplete'})
      getActivities()

      const tenantRef = doc(db, 'tenants', currentUser?.tenantId!)
      const activitiesRef = collection(tenantRef, 'activities')
      await addDoc(activitiesRef, {
        message: `${currentUser?.userName} has marked the task '${taskName}' as Incomplete `,
        timestamp: serverTimestamp(),
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        action: 'Control added',
        target: 'Controls',
        email: currentUser?.email,
        username: currentUser?.userName,
        date: todaysDate,
      })

      showSuccessToast({message: 'Task has been marked as Incomplete!'})
    } catch (error) {
      console.error('Error updating task status:', error)
    }
  }

  const handleRemoveTask = (taskId: string, task: any) => {
    setToDeleteTask(task)
    setIsDeleteModalOpen(true)
  }

  const removeTask = async () => {
    try {
      setIsDeleting(true)
      const tenantId = currentUser?.tenantId || ''
      const taskRef = doc(db, 'tenants', tenantId, 'tasklists', toDeleteTask.id)
      await deleteDoc(taskRef)
      getActivities()
      showSuccessToast({message: `${toDeleteTask.task_name} have been removed successfully`})
    } catch (error) {
      showErrorToast({message: 'An error occurred while removing the selected items.'})
    } finally {
      setIsDeleting(false)
      setIsDeleteModalOpen(false)
    }
  }

  const formatDate = (dateInput: string | number): string => {
    try {
      let date: Date

      // Handle empty or null input
      if (!dateInput) return ''

      // Handle number or numeric string (assumed to be timestamp in ms)
      if (
        typeof dateInput === 'number' ||
        (!isNaN(Number(dateInput)) && dateInput.toString().length >= 10)
      ) {
        const timestamp = Number(dateInput)
        date = new Date(timestamp < 1e12 ? timestamp * 1000 : timestamp)
      }
      // Handle YYYY-MM-DD format
      else if (typeof dateInput === 'string') {
        if (dateInput.includes('-')) {
          const [year, month, day] = dateInput.split('-').map(Number)
          if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
            date = new Date(year, month - 1, day)
          } else {
            return ''
          }
        } else {
          date = new Date(dateInput)
        }
      } else {
        return ''
      }

      if (isNaN(date.getTime())) return '' // Invalid date

      const formattedDay = date.getDate()
      const formattedMonth = date.toLocaleString('default', {month: 'long'})
      const formattedYear = date.getFullYear()
      return `${formattedDay} ${formattedMonth}, ${formattedYear}`
    } catch (error) {
      console.error('Error formatting date:', error)
      return ''
    }
  }

  const handleOpenModal2 = (id) => {
    setIsModalOpen2(true)
    setTaskID(id)
  }
  const handleCloseModal2 = () => {
    setIsModalOpen2(false)
  }

  const handleMarkSelectedAsComplete = async () => {
    const tenantId = currentUser?.tenantId || ''

    for (const taskId of checkedTasks) {
      try {
        const taskRef = doc(db, 'tenants', tenantId, 'tasklists', taskId)
        await updateDoc(taskRef, {status: 'Completed'})
      } catch (error) {
        console.error(`Error updating task ${taskId} status:`, error)
      }
    }

    getActivities()

    setCheckedTasks([])
  }

  const handleOpenModal = () => {
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
  }

  const getEmptyStateMessage = () => {
    switch (subTab) {
      case 'open':
        return 'No open tasks found.'
      case 'completed':
        return 'No completed tasks found.'
      case 'pending':
        return 'No pending tasks found.'
      case 'overdue':
        return 'No overdue tasks found.'
      case 'myTasks':
        return 'No tasks assigned to you found.'
      default:
        return 'No tasks found.'
    }
  }

  const getTaskCountMessage = () => {
    const count = filteredTasks.length
    switch (subTab) {
      case 'open':
        return `${count} open tasks`
      case 'completed':
        return `${count} completed tasks`
      case 'pending':
        return `${count} pending tasks`
      case 'overdue':
        return `${count} overdue tasks`
      case 'myTasks':
        return `${count} my tasks`
      default:
        return `${count} tasks`
    }
  }

  const handleSubTabClick = (tab) => {
    setSubTab(tab)
  }

  const renderPriority = (priority: any) => {
    if (!priority) return '-'
    return typeof priority === 'object' ? priority.priority : priority
  }

  // const handleExportTrialUsers = async () => {
  //   try {
  //     if (!currentUser?.tenantId) {
  //       showErrorToast({message: 'User information not available'})
  //       return
  //     }

  //     const currentDate = new Date()
  //     const formattedDate = currentDate.toISOString().split('T')[0] // YYYY-MM-DD format
  //     const auditFolderName = `trial-users-export-${formattedDate}`
  //     const folderId = 'trial-users-folder' 

  //     const result = await ExportTrialUsers(
  //       {
  //         tenantId: currentUser.tenantId,
  //         uid: currentUser.uid,
  //         userName: currentUser.userName || undefined,
  //       },
  //       auditFolderName,
  //       folderId,
  //       formattedDate,
  //       '' // No specific audit ID for this export
  //     )

  //     showSuccessToast({message: 'Trial users data exported successfully!'})

  //     // Optionally open the download URL
  //     window.open(result.downloadUrl, '_blank')
  //   } catch (error) {
  //     console.error('Export failed:', error)
  //     showErrorToast({message: 'Failed to export trial users data'})
  //   }
  // }

  return (
    <>
      <div className='card' id='kt_timeline_widget_2_card'>
        {/* // h-xl-100 */}
        <div
          className='d-flex justify-content-between px-8 border-0 pt-6'
          style={{position: 'sticky', top: 0, zIndex: 1, background: 'white', borderRadius: '6px'}}
        >
          <h3>
            <div className='card-label fw-bold fs-4 mb-1'>Task Lists</div>
            <div className='text-gray-700 mt-1 fw-semibold fs-7'>{getTaskCountMessage()}</div>
          </h3>
          {checkedTasks.length > 0 ? (
            <>
              <div
                className='card-toolbar'
                data-bs-toggle='tooltip'
                data-bs-placement='top'
                data-bs-trigger='hover'
                title='Click to mark as complete'
              >
                <button
                  className='btn btn-sm'
                  onClick={handleMarkSelectedAsComplete}
                  onMouseEnter={(e: React.MouseEvent<HTMLElement>) =>
                    (e.currentTarget.style.backgroundColor = '#148363')
                  }
                  onMouseLeave={(e: React.MouseEvent<HTMLElement>) =>
                    (e.currentTarget.style.backgroundColor = '#0E9F6E')
                  }
                  style={{
                    backgroundColor: '#0E9F6E',
                    color: '#fff',
                    height: '40px',
                    borderRadius: '4px',

                    transition: 'background-color 0.3s',
                  }}
                >
                  Mark as complete
                </button>
              </div>
            </>
          ) : (
            <div className='d-flex gap-3'>
              <div
                className='card-toolbar'
                data-bs-toggle='tooltip'
                data-bs-placement='top'
                title='Click to add a task'
              >
                <Button
                  btnType='primary'
                  onClick={handleOpenModal}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='18'
                    height='18'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    stroke-width='2'
                    stroke-linecap='round'
                    stroke-linejoin='round'
                    className='icon icon-tabler icons-tabler-outline icon-tabler-plus me-2'
                  >
                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                    <path d='M12 5l0 14' />
                    <path d='M5 12l14 0' />
                  </svg>
                  Add Task
                </Button>
              </div>
              {/* <div
                className='card-toolbar'
                data-bs-toggle='tooltip'
                data-bs-placement='top'
                title='Export trial users data'
              >
                <ExportButton onClick={handleExportTrialUsers} />
              </div> */}
            </div>
          )}
        </div>
        {/* begin::Header */}

        <div className=' border-bottom pb-4 '>
          {/* begin::Nav */}

          <ul
            className='nav nav-stretch nav-pills nav-pills-custom d-flex mt-4 px-8'
            role='tablist'
          >
            <li className='nav-item p-0 ms-0 me-8' role='Open'>
              <a
                data-bs-toggle='pill'
                href='#kt_timeline_widget_2_tab_2'
                aria-selected='false'
                tabIndex={-1}
                role='tab'
                className={`nav-link btn btn-color-muted ${subTab === 'open' ? 'active' : ''} p-0`}
                onClick={() => handleSubTabClick('open')}
              >
                <span
                  className='fw-semibold fs-6'
                  style={{color: subTab === 'open' ? '#0E9F6E' : '#737373'}}
                >
                  All Tasks
                </span>

                <span
                  className='bullet-custom position-absolute z-index-2 w-100 mt-4 h-2px top-100 bottom-100 rounded'
                  style={{background: '#0E9F6E'}}
                ></span>
              </a>
            </li>
            <li className='nav-item p-0 ms-0 me-8' role='completed'>
              <a
                data-bs-toggle='pill'
                href='#kt_timeline_widget_2_tab_3'
                aria-selected='false'
                tabIndex={-1}
                role='tab'
                className={`nav-link btn btn-color-muted ${
                  subTab === 'pending' ? 'active' : ''
                } p-0`}
                onClick={() => handleSubTabClick('pending')}
              >
                <span
                  className='fw-semibold fs-6'
                  style={{color: subTab === 'pending' ? '#0E9F6E' : '#737373'}}
                >
                  Pending
                </span>
                <span
                  className='bullet-custom position-absolute z-index-2 w-100 mt-4 h-2px top-100 bottom-100 rounded'
                  style={{background: '#0E9F6E'}}
                ></span>
              </a>
            </li>
            <li className='nav-item p-0 ms-0 me-8' role='completed'>
              <a
                data-bs-toggle='pill'
                href='#kt_timeline_widget_2_tab_3'
                aria-selected='false'
                tabIndex={-1}
                role='tab'
                className={`nav-link btn btn-color-muted ${
                  subTab === 'completed' ? 'active' : ''
                } p-0`}
                onClick={() => handleSubTabClick('completed')}
              >
                <span
                  className='fw-semibold fs-6'
                  style={{color: subTab === 'completed' ? '#0E9F6E' : '#737373'}}
                >
                  Completed
                </span>
                <span
                  className='bullet-custom position-absolute z-index-2 w-100 mt-4 h-2px top-100 bottom-100 rounded'
                  style={{background: '#0E9F6E'}}
                ></span>
              </a>
            </li>
            <li className='nav-item p-0 ms-0 me-8' role='completed'>
              <a
                data-bs-toggle='pill'
                href='#kt_timeline_widget_2_tab_3'
                aria-selected='false'
                tabIndex={-1}
                role='tab'
                className={`nav-link btn btn-color-muted ${
                  subTab === 'overdue' ? 'active' : ''
                } p-0`}
                onClick={() => handleSubTabClick('overdue')}
              >
                <span
                  className='fw-semibold fs-6'
                  style={{color: subTab === 'overdue' ? '#0E9F6E' : '#737373'}}
                >
                  Overdue
                </span>
                <span
                  className='bullet-custom position-absolute z-index-2 w-100 mt-4 h-2px top-100 bottom-100 rounded'
                  style={{background: '#0E9F6E'}}
                ></span>
              </a>
            </li>
            <li className='nav-item p-0 ms-0 me-8' role='completed'>
              <a
                data-bs-toggle='pill'
                href='#kt_timeline_widget_2_tab_3'
                aria-selected='false'
                tabIndex={-1}
                role='tab'
                className={`nav-link btn btn-color-muted ${
                  subTab === 'myTasks' ? 'active' : ''
                } p-0`}
                onClick={() => handleSubTabClick('myTasks')}
              >
                <span
                  className='fw-semibold fs-6 '
                  style={{color: subTab === 'myTasks' ? '#0E9F6E' : '#737373'}}
                >
                  My Tasks
                </span>
                <span
                  className='bullet-custom position-absolute z-index-2 w-100 mt-4 h-2px top-100 bottom-100 rounded'
                  style={{background: '#0E9F6E'}}
                ></span>
              </a>
            </li>
          </ul>
          {/* end::Nav */}
        </div>
        {/* end::Header */}

        {/* begin::Body */}
        <div className='card-body py-0 px-0'>
          {/* begin::Tab Content */}
          <div className='tab-content'>
            {/* begin::Tap pane */}
            {/* {filteredTasks.length > 0 ? ( */}
            <div className='tab-pane fade show active' role='tabpanel'>
              {/* begin::Table container */}
              <div className='table-responsive' style={{maxHeight: '600px', minHeight: '400px'}}>
                {/* begin::Table */}
                <table className='table align-middle gs-0 gy-4 mb-0'>
                  {/* begin::Table head */}
                  <thead className='bg-white' style={{position: 'sticky', top: 0, zIndex: 1}}>
                    <tr
                      className='fw-bold border-bottom fs-7'
                      style={{color: '#5A5A5A', background: '#FAFAFA', padding: '0 24px'}}
                    >
                      <th className='min-w-350px px-8' onClick={() => handleSort('task_name')}>
                        TASKS {renderSortIcon('task_name')}
                      </th>
                      <th className='min-w-100px ' onClick={() => handleSort('priority')}>
                        PRIORITY {renderSortIcon('priority')}
                      </th>
                      <th className='min-w-200px' onClick={() => handleSort('owner')}>
                        ASSIGNED TO {renderSortIcon('owner')}
                      </th>
                      <th className='min-w-150px' onClick={() => handleSort('duedate')}>
                        DUE DATE {renderSortIcon('duedate')}
                      </th>
                      <th className='min-w-150px' onClick={() => handleSort('status')}>
                        STATUS {renderSortIcon('status')}
                      </th>
                      <th className='min-w-50px text-end'></th>
                    </tr>
                  </thead>
                  {/* end::Table head */}

                  {/* begin::Table body */}

                  <tbody style={{maxHeight: '550px', overflowY: 'auto'}}>
                    {filteredTasks.length > 0 ? (
                      filteredTasks
                        .sort((a, b) => {
                          const key = sortBy
                          const direction = sortDirection === 'asc' ? 1 : -1

                          // Special sorting for priority
                          if (key === 'priority') {
                            return direction * (priorityOrder[a[key]] - priorityOrder[b[key]])
                          }

                          if (key === 'duedate') {
                            // Special case for date sorting
                            const dateA = new Date(a[key]).getTime()
                            const dateB = new Date(b[key]).getTime()
                            return (dateA - dateB) * direction
                          } else if (a[key] < b[key]) {
                            return -1 * direction
                          } else if (a[key] > b[key]) {
                            return 1 * direction
                          } else {
                            return 0
                          }
                        })
                        .map((task) => (
                          <tr
                            key={task.id}
                            className='hover:bg-gray-100 transition-colors duration-200'
                            style={{display: 'table-row'}}
                          >
                            <td className='px-8'>
                              <div className='d-flex '>
                                <div className='form-check form-check-sm form-check-custom form-check-solid me-4'>
                                  <input
                                    className='form-check-input widget-9-check rounded-circle'
                                    type='checkbox'
                                    data-bs-toggle='tooltip'
                                    data-bs-placement='top'
                                    title='Click to mark as complete/incomplete'
                                    checked={task.status === 'Completed'}
                                    onChange={() => {
                                      if (task.status === 'Completed') {
                                        handleMarkAsIncomplete(task.id, task.task_name)
                                      } else {
                                        handleMarkAsComplete(task.id, task.task_name)
                                      }
                                    }}
                                  />
                                </div>
                                <div
                                  className='text-dark fw-bold text-hover-primary d-block fs-6'
                                  style={{cursor: 'pointer'}}
                                  onClick={() => handleOpenModal2(task.id)}
                                >
                                  {task.task_name}
                                </div>
                              </div>
                            </td>
                            <td>
                              <a
                                href='#'
                                className={`badge fs-7 ${
                                  task.priority === 'High'
                                    ? 'badge-danger'
                                    : task.priority === 'Medium'
                                    ? 'badge-warning'
                                    : 'badge-primary'
                                }`}
                                style={{
                                  color:
                                    task.priority === 'High'
                                      ? '#EE3043'
                                      : task.priority === 'Medium'
                                      ? '#FFA800'
                                      : '#0E9F6E',
                                  background:
                                    task.priority === 'High'
                                      ? '#FFF5F8'
                                      : task.priority === 'Medium'
                                      ? '#FFF8DD'
                                      : '#E8F8F4',
                                }}
                              >
                                <span>{renderPriority(task.priority)}</span>
                              </a>
                            </td>
                            <td>
                              <div className='d-flex align-items-center'>
                                {task.owner ? (
                                  <>
                                    <OwnerDisplay owner={task.owner} uid={task.assignee_id} />
                                  </>
                                ) : (
                                  <>
                                    <span>-</span>
                                  </>
                                )}
                              </div>
                            </td>

                            <td className='fs-6 fw-semibold'> {FormatTimestamp({timestamp:task.duedate})}</td>
                            <td>
                              <span
                                className='badge fs-7'
                                style={{
                                  color: task.status === 'Completed' ? '#0E9F6E' : '#EE3043',
                                  background: task.status === 'Completed' ? '#E8F8F4' : '#FFF5F8',
                                }}
                              >
                                {' '}
                                {task.status}
                              </span>
                            </td>
                            <td className='pe-2'>
                              <div className='dropdown'>
                                <button
                                  className='btn btn-sm btn-icon border bg-gray-100'
                                  type='button'
                                  id='dropdownMenu2'
                                  data-bs-toggle='dropdown'
                                  aria-haspopup='true'
                                  aria-expanded='false'
                                >
                                  <svg
                                    xmlns='http://www.w3.org/2000/svg'
                                    width='16'
                                    height='16'
                                    viewBox='0 0 24 24'
                                    fill='none'
                                    stroke='currentColor'
                                    stroke-width='2'
                                    stroke-linecap='round'
                                    stroke-linejoin='round'
                                    className='icon icon-tabler icons-tabler-outline icon-tabler-dots'
                                  >
                                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                    <path d='M5 12m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0' />
                                    <path d='M12 12m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0' />
                                    <path d='M19 12m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0' />
                                  </svg>
                                </button>
                                {/* {isDropdownVisible && ( */}
                                <ul
                                  className='dropdown-menu px-2 fs-6'
                                  aria-labelledby='dropdownMenu2'
                                  style={{borderRadius: '8px'}}
                                >
                                  <li>
                                    <button
                                      type='button'
                                      onClick={() => handleOpenModal2(task.id)}
                                      className='dropdown-item d-flex align-items-center justify-content-start hover:bg-gray-100 transition-colors duration-150'
                                      style={{color: '#5A5A5A'}}
                                    >
                                      <svg
                                        xmlns='http://www.w3.org/2000/svg'
                                        width='18'
                                        height='18'
                                        viewBox='0 0 24 24'
                                        fill='none'
                                        stroke='currentColor'
                                        stroke-width='2'
                                        stroke-linecap='round'
                                        stroke-linejoin='round'
                                        className='icon icon-tabler icons-tabler-outline icon-tabler-edit me-2'
                                      >
                                        <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                        <path d='M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1' />
                                        <path d='M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z' />
                                        <path d='M16 5l3 3' />
                                      </svg>{' '}
                                      Edit task
                                    </button>
                                  </li>
                                  <li>
                                    {task.status === 'Completed' ? (
                                      <button
                                        className='dropdown-item d-flex align-items-center justify-content-start hover:bg-gray-100 transition-colors duration-150'
                                        style={{color: '#5A5A5A'}}
                                        type='button'
                                        onClick={() =>
                                          handleMarkAsIncomplete(task.id, task.task_name)
                                        }
                                      >
                                        <svg
                                          xmlns='http://www.w3.org/2000/svg'
                                          width='18'
                                          height='18'
                                          viewBox='0 0 24 24'
                                          fill='none'
                                          stroke='currentColor'
                                          stroke-width='2'
                                          stroke-linecap='round'
                                          stroke-linejoin='round'
                                          className='icon icon-tabler icons-tabler-outline icon-tabler-x me-2'
                                        >
                                          <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                          <path d='M18 6l-12 12' />
                                          <path d='M6 6l12 12' />
                                        </svg>{' '}
                                        Mark as Incomplete
                                      </button>
                                    ) : (
                                      <button
                                        className='dropdown-item d-flex align-items-center justify-content-start hover:bg-gray-100 transition-colors duration-150'
                                        style={{color: '#5A5A5A'}}
                                        type='button'
                                        onClick={() =>
                                          handleMarkAsComplete(task.id, task.task_name)
                                        }
                                      >
                                        <svg
                                          xmlns='http://www.w3.org/2000/svg'
                                          width='18'
                                          height='18'
                                          viewBox='0 0 24 24'
                                          fill='none'
                                          stroke='#5E6274'
                                          strokeWidth='2'
                                          strokeLinecap='round'
                                          strokeLinejoin='round'
                                          className='icon icon-tabler icons-tabler-outline icon-tabler-check me-2 '
                                        >
                                          <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                          <path d='M5 12l5 5l10 -10' />
                                        </svg>{' '}
                                        Mark as complete
                                      </button>
                                    )}
                                  </li>
                                  <div className='separator my-2'></div>
                                  <li>
                                    <button
                                      className='dropdown-item d-flex align-items-center justify-content-start hover:bg-gray-100 transition-colors duration-150'
                                      style={{color: '#F15363'}}
                                      type='button'
                                      onClick={() => handleRemoveTask(task.id, task)}
                                    >
                                      <svg
                                        xmlns='http://www.w3.org/2000/svg'
                                        width='18'
                                        height='18'
                                        viewBox='0 0 24 24'
                                        fill='none'
                                        stroke='#F15363'
                                        strokeWidth='2'
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        className='icon icon-tabler icons-tabler-outline icon-tabler-trash me-2'
                                      >
                                        <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                        <path d='M4 7l16 0' />
                                        <path d='M10 11l0 6' />
                                        <path d='M14 11l0 6' />
                                        <path d='M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12' />
                                        <path d='M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3' />
                                      </svg>{' '}
                                      Delete task
                                    </button>
                                  </li>
                                </ul>
                                {/* )} */}
                              </div>
                            </td>
                          </tr>
                        ))
                    ) : (
                      <>
                        <tr>
                          {!showEmptyState ? (
                            <td colSpan={10}>
                              <div
                                className='d-flex justify-content-center align-items-center text-center'
                                style={{height: '400px'}}
                              >
                                <TestListLoading />
                              </div>
                            </td>
                          ) : (
                            <td colSpan={10}>
                              <div
                                className='d-flex justify-content-center align-items-center text-center'
                                style={{height: '100%'}}
                              >
                                <div className='m-20'>
                                  <img src='/media/buttons/emptystate.svg' alt='' />

                                  <div className='mt-4 fs-6'>
                                    <p className='semibold'>{getEmptyStateMessage()}</p>
                                  </div>
                                </div>
                              </div>
                            </td>
                          )}
                        </tr>
                      </>
                    )}
                  </tbody>

                  {/* end::Table body */}
                </table>
                {/* end::Table */}
              </div>
              {/* end::Table container */}
            </div>
          </div>
          {/* end::Tab Content */}
        </div>
        {/* end::Body */}
      </div>
      <AddTask isOpen={isModalOpen} onClose={handleCloseModal} />
      <EditTask id={taskID} isOpen={isModalOpen2} onClose={handleCloseModal2} />
      {isDeleteModalOpen && (
        <ConfirmationModal
          isModalOpen={isDeleteModalOpen}
          hideModal={() => setIsDeleteModalOpen(false)}
          confirmBtnClickHandler={removeTask}
          toDeleteItemTitle={toDeleteTask?.task_name}
          isLoading={isDeleting}
          type='delete'
          confirmButtonText='Remove'
        />
      )}
    </>
  )
}
export {TaskList}
